import React, {Fragment, useEffect, useContext} from 'react';
import glamorous from 'glamorous';
import PropTypes from 'prop-types';
import ProsConsCard, {PROS, FULL} from '../../../shared/library/cards/pros-cons';
import AlternateDecisions from '../../../shared/library/cards/alternate-decision';
import {Query, compose} from 'react-apollo';
import {toolDecisions, posts} from '../../../data/tool-profile/queries';
import Circular, {LARGE} from '../../../shared/library/indicators/indeterminate/circular';
import {ComponentContainer, Spinner, StyledSmallTitle} from './shared/styles';
import NavAnchor, {NAV_ANCHOR_OFFSET} from '../../tool-profile/components/shared/nav-anchor';
import {flattenEdges} from '../../../shared/utils/graphql';
import {withMutation} from '../../../shared/enhancers/graphql-enhancer';
import {trackViews} from '../../../data/shared/mutations';
import {BASE_TEXT, TITLE_TEXT} from '../../../shared/style/typography';
import {TARMAC} from '../../../shared/style/colors';
import {withAnalyticsPayload} from '../../../shared/enhancers/analytics-enhancer';
import {updateQuery} from '../utils';
import withErrorBoundary from '../../../shared/enhancers/error-boundary';
import {callTrackViews} from '../../../../app/shared/utils/trackViews.js';
import BlogPost from '../../../shared/library/cards/blogpost';
import {MobileContext} from '../../../shared/enhancers/mobile-enhancer';

const Container = glamorous.div({
  margin: '50px 0 0 0'
});

const Paragraph = glamorous.p({
  color: TARMAC,
  ...BASE_TEXT,
  marginTop: 8,
  lineHeight: 1.69
});

const sendTrackViewsMutation = ({loading, decisions, trackViews}) => {
  if (!loading && decisions) {
    const decisionIds = decisions.map(d => d.id);
    callTrackViews({trackViews, decisionIds, clientContext: `Reasons-${window.location.pathname}`});
  }
};

const Title = glamorous.div({
  ...TITLE_TEXT,
  fontWeight: 600,
  fontSize: 18,
  lineHeight: 1.7,
  marginBottom: 25
});

const Reasons = ({tool, trackViews, privateMode, showPros, showPosts, currentUser}) => {
  const isMobile = useContext(MobileContext);
  return (
    <ComponentContainer>
      {showPros && (
        <>
          <NavAnchor id="pros" offset={NAV_ANCHOR_OFFSET} />
          <Container>
            <Title>Pros of {tool.name}</Title>
            <ProsConsCard
              privateMode={privateMode}
              type={PROS}
              slug={tool.slug}
              name={tool.name}
              items={tool[PROS]}
              theme={FULL}
              hasMultipleColumns={true}
            />
          </Container>
        </>
      )}

      <Query query={toolDecisions} variables={{id: tool.id}} notifyOnNetworkStatusChange={true}>
        {({loading, data}) => {
          const hasData = data && data.tool;
          if (loading && !hasData)
            return (
              <Spinner>
                <Circular size={LARGE} />
              </Spinner>
            );

          const decisions = flattenEdges(data.tool.stackDecisions);
          if (hasData && decisions.length >= 1) {
            return (
              <PublicDecisions
                loading={loading}
                decisions={decisions}
                trackViews={trackViews}
                privateMode={privateMode}
                tool={tool}
              />
            );
          } else {
            return null;
          }
        }}
      </Query>
      {tool && tool.name && showPosts && (
        <Query
          query={posts}
          variables={{id: tool.slug, first: 8}}
          notifyOnNetworkStatusChange={true}
        >
          {({loading, data, fetchMore}) => {
            const hasData = data && data.tool;
            if (loading && !hasData)
              return (
                <Spinner>
                  <Circular size={LARGE} />
                </Spinner>
              );

            if (hasData) {
              const posts = flattenEdges(data.tool.featuredPosts);
              const endCursor = data.tool.featuredPosts.pageInfo.endCursor;
              const hasNextPage = data.tool.featuredPosts.pageInfo.hasNextPage;

              const onLoadMoreClick = () => {
                fetchMore({
                  variables: {
                    after: endCursor,
                    first: isMobile ? 6 : 10
                  },
                  updateQuery
                });
              };

              if (posts && posts.length >= 1)
                return (
                  <Container>
                    <NavAnchor id="posts" offset={NAV_ANCHOR_OFFSET} />
                    <BlogPost
                      posts={posts}
                      title={'Blog Posts'}
                      hasNextPage={hasNextPage}
                      loadMore={onLoadMoreClick}
                      loading={loading}
                      currentUser={currentUser}
                    />
                  </Container>
                );
              else return null;
            } else return null;
          }}
        </Query>
      )}
    </ComponentContainer>
  );
};

Reasons.propTypes = {
  tool: PropTypes.object,
  trackViews: PropTypes.func,
  privateMode: PropTypes.bool,
  showPros: PropTypes.bool,
  showPosts: PropTypes.bool,
  currentUser: PropTypes.object
};

const PublicDecisions = ({loading, decisions, trackViews, privateMode, tool}) => {
  useEffect(() => {
    sendTrackViewsMutation({loading, decisions, trackViews});
  }, [decisions]);

  return (
    <Container>
      {decisions.length !== 0 && (
        <Fragment>
          <StyledSmallTitle>
            {privateMode ? `Public Decisions about ${tool.name}` : `Decisions about ${tool.name}`}
          </StyledSmallTitle>
          <Paragraph>
            Here are some stack decisions, common use cases and reviews by companies and developers
            who chose {tool.name} in their tech stack.
          </Paragraph>
        </Fragment>
      )}
      <AlternateDecisions
        decisions={decisions}
        allDecisionsLink={`/tool/${tool.slug}/decisions`}
        sendAnalyticsEvent={() => {}}
      />
    </Container>
  );
};

PublicDecisions.propTypes = {
  loading: PropTypes.bool,
  decisions: PropTypes.array,
  trackViews: PropTypes.func,
  privateMode: PropTypes.bool,
  tool: PropTypes.object
};

export default compose(
  withAnalyticsPayload(),
  withErrorBoundary(true),
  withMutation(trackViews, mutate => ({
    trackViews: (decisionIds, clientContext) => mutate({variables: {decisionIds, clientContext}})
  }))
)(Reasons);
